import { h } from '@stencil/core';
import { triggerTrackEvent } from 'utils/events';
import { ImageSliderManager } from 'utils/ImageSliderManager';
import { getThumbnailUrl, getFullSizeUrl } from 'utils/imageUtils';
import galleryModal from 'store/galleryModal';
import Swiper from 'swiper';
import { ChatImage } from 'components/common/ChatImage';

let swiperInstances: [Swiper, Swiper];
let modalRef: HTMLElement | null = null;
const imageSliderManager = ImageSliderManager.getInstance();

const onClose = () => {
  galleryModal.isOpen = false;

  // Clean up sliders when modal closes to prevent memory leaks
  if (modalRef) {
    const mainContainer = modalRef.querySelector('#swiper-container-main') as HTMLElement;
    const thumbContainer = modalRef.querySelector('#swiper-container-thumb') as HTMLElement;

    if (mainContainer) imageSliderManager.destroySlider(mainContainer);
    if (thumbContainer) imageSliderManager.destroySlider(thumbContainer);
  }

  triggerTrackEvent({
    event_type: 'user',
    event_name: 'close_gallery',
  });
};

const onKeyDown = event => {
  const [thumbs, swiperInstance] = swiperInstances;

  if (event.key === 'ArrowRight') {
    event.preventDefault();

    if (!swiperInstance.isEnd) {
      triggerTrackEvent({
        event_type: 'user',
        event_name: 'gallery_next_image_keyboard',
      });
    }

    swiperInstance.slideNext();
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();

    if (!swiperInstance.isBeginning) {
      triggerTrackEvent({
        event_type: 'user',
        event_name: 'gallery_prev_image_keyboard',
      });
    }

    swiperInstance.slidePrev();
  } else if (event.key === 'Escape') {
    triggerTrackEvent({
      event_type: 'user',
      event_name: 'gallery_close_keyboard',
    });
    event.preventDefault();
    onClose();
  }
};

export default function GalleryModal({ show }: { show: boolean }) {
  requestAnimationFrame(() => {
    if (show && modalRef) {
      // Use ImageSliderManager singleton for better memory management
      const mainContainer = modalRef.querySelector('#swiper-container-main') as HTMLElement;
      const thumbContainer = modalRef.querySelector('#swiper-container-thumb') as HTMLElement;

      // Clean up any existing sliders first
      if (mainContainer) imageSliderManager.destroySlider(mainContainer);
      if (thumbContainer) imageSliderManager.destroySlider(thumbContainer);

      // Initialize synchronized sliders
      swiperInstances = imageSliderManager.initSliderWithThumbs(mainContainer, thumbContainer);
    }

    document[show ? 'addEventListener' : 'removeEventListener']('keydown', onKeyDown);
  });

  return show ? (
    <div ref={el => (modalRef = el || null)} id="gallery-modal" role="presentation" class="eva-3-modal -show-sticky-footer fullscreen-modal -close-icon-white -show-modal">
      <div role="presentation" class="modal-header transparent-modal">
        <i class="eva-3-icon-cross modal-close -eva-3-mr-md" role="button" tabindex="0" aria-label="Close modal" onClick={() => onClose()} />
      </div>
      <div role="presentation" class="modal-content transparent-modal">
        <div id="swiper-container-main" class="swiper-container">
          <div class="swiper">
            <div class="swiper-wrapper">
              {galleryModal.images.map(image => (
                <ChatImage class="swiper-slide" src={getFullSizeUrl(image.url)} alt="Image" />
              ))}
            </div>
          </div>
        </div>
        <div id="swiper-container-thumb" class="swiper-container">
          <div class="swiper">
            <div class="swiper-wrapper">
              {galleryModal.images.map(image => (
                <ChatImage class="swiper-slide" src={getThumbnailUrl(image.url)} alt="Thumb image" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
